/**
 * @file ErrorBoundary.tsx
 * @description Top-level React error boundary.
 * Catches all unhandled render errors, shows a friendly UI,
 * and auto-POSTs a bug report to /api/v1/bugs for the App Owner
 * to view in the Bug Reports card.
 */
import { Component, type ReactNode, type ErrorInfo } from 'react';
import { isChunkLoadError, attemptChunkReload } from '../pwa/chunkReload';

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string;
  errorStack: string;
  referenceId: string;
  /**
   * PWA-UPDATE-1. A lazy chunk that no longer exists on the CDN arrives here as a render error, and
   * it is not a bug in our code — it is a deploy that moved underneath an open tab. Treating it like
   * a crash means a full-screen "Something went wrong" and a bug report, when the correct response is
   * simply to reload onto the new build.
   */
  isChunkError: boolean;
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

/** A short, human-quotable reference code for the crash (client-side; full details still go to the bug report). */
function genRef(): string {
  try {
    const uuid = (globalThis.crypto as Crypto & { randomUUID?: () => string })?.randomUUID?.();
    if (uuid) return uuid.replace(/-/g, '').slice(0, 8).toUpperCase();
  } catch { /* fall through to the non-crypto path */ }
  return Math.random().toString(36).slice(2, 10).toUpperCase();
}

/**
 * ErrorBoundary
 * Class component (required by React's error boundary API).
 * On error: shows a recovery UI and silently POSTs to /api/v1/bugs.
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: '', errorStack: '', referenceId: '', isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      errorMessage: error?.message ?? 'Unknown error',
      errorStack: error?.stack ?? '',
      referenceId: genRef(),
      // Stays pure — classification only, no side effect. The reload happens in componentDidCatch.
      isChunkError: isChunkLoadError(error?.message ?? ''),
    };
  }

  /** True once a chunk reload has actually been started, so render() knows a blank frame will end. */
  private chunkReloadStarted = false;

  componentDidCatch(error: Error, info: ErrorInfo) {
    // A dead chunk is a deploy artefact, not a defect, so it must not become a bug report. If the
    // breaker refuses (this already happened within the last minute), fall through: at that point it
    // IS a real repeating failure and the user deserves the crash card rather than a reload loop.
    if (this.state.isChunkError && attemptChunkReload()) {
      this.chunkReloadStarted = true;
      this.forceUpdate();
      return;
    }
    this.postBugReport(error, info);
  }

  /** POST the crash report to the backend. Fire-and-forget — never throw. */
  private postBugReport(error: Error, info: ErrorInfo) {
    try {
      const token = this.tryReadLocalStorage('schoolsaas_token');
      const payload = {
        errorMessage: error?.message ?? 'Unknown error',
        stackTrace:   (error?.stack ?? '') + (info?.componentStack ? '\n\nComponent Stack:\n' + info.componentStack : ''),
        pageUrl:      window.location.href,
        browserInfo:  navigator.userAgent,
        deviceInfo:   navigator.platform ?? '',
        breadcrumbs:  [],
      };

      fetch('/api/v1/bug-reports', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => { /* swallow silently */ });
    } catch {
      // Never let the bug reporter itself crash the page
    }
  }

  private tryReadLocalStorage(key: string): string {
    try { return localStorage.getItem(key) ?? ''; }
    catch { return ''; }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    this.setState({ hasError: false, errorMessage: '', errorStack: '' });
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // A reload is already in flight (componentDidCatch started it). Render a blank frame for the
    // ~200ms until it lands rather than flashing a crash card the user would have no reason to trust
    // afterwards. If the breaker refused, isChunkError is still true but no reload was started — so
    // this would hang on blank. Guard on it: only blank when a reload is genuinely coming.
    if (this.state.isChunkError && this.chunkReloadStarted) return null;

    return (
      <div
        style={{
          minHeight: '100vh',
          background: 'var(--bg)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          fontFamily: 'var(--font-body)',
        }}
      >
        <div
          style={{
            background: 'var(--surface)',
            border: '1.5px solid var(--border)',
            borderRadius: 24,
            padding: 32,
            maxWidth: 480,
            width: '100%',
            boxShadow: 'var(--shadow-lg)',
            textAlign: 'center',
          }}
        >
          {/* Icon */}
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 20,
              background: 'var(--red-lt)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 28,
              margin: '0 auto 20px',
            }}
          >
            😕
          </div>

          {/* Title */}
          <h1
            style={{
              fontFamily: 'var(--font-heading)',
              fontSize: 22,
              fontWeight: 800,
              color: 'var(--t1)',
              marginBottom: 10,
              letterSpacing: '-0.02em',
            }}
          >
            Something went wrong
          </h1>

          <p
            style={{
              fontSize: 13,
              color: 'var(--t2)',
              lineHeight: 1.6,
              marginBottom: 24,
            }}
          >
            An unexpected error occurred. Our team has been notified automatically.
            Please reload the page or return to the home screen.
          </p>

          {/* Reference code — a plain, quotable id. The raw error message/stack is NOT shown to the
              user; it still flows in full to the bug report POST above for debugging. */}
          {this.state.referenceId && (
            <div
              style={{
                background: 'var(--surface-2)',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '10px 14px',
                marginBottom: 20,
                textAlign: 'center',
              }}
            >
              <div style={{ fontSize: 11, color: 'var(--t3)', marginBottom: 3 }}>Reference code</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--t1)', fontFamily: 'monospace', letterSpacing: '0.1em' }}>
                {this.state.referenceId}
              </div>
              <div style={{ fontSize: 10.5, color: 'var(--t4)', marginTop: 5, lineHeight: 1.4 }}>
                Share this code with support if the problem continues.
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button
              onClick={this.handleReload}
              style={{
                background: 'linear-gradient(135deg,#4F46E5,#7C3AED)',
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                height: 44,
                fontSize: 14,
                fontWeight: 700,
                fontFamily: 'var(--font-body)',
                cursor: 'pointer',
                minHeight: 44,
              }}
            >
              🔄 Reload Page
            </button>
            <button
              onClick={this.handleGoHome}
              style={{
                background: 'transparent',
                color: 'var(--t2)',
                border: '1.5px solid var(--border)',
                borderRadius: 12,
                height: 44,
                fontSize: 14,
                fontWeight: 600,
                fontFamily: 'var(--font-body)',
                cursor: 'pointer',
                minHeight: 44,
              }}
            >
              🏠 Go to Home Screen
            </button>
          </div>

          <p
            style={{
              fontSize: 11,
              color: 'var(--t4)',
              marginTop: 16,
            }}
          >
            Error has been automatically reported to support.
          </p>
        </div>
      </div>
    );
  }
}